import { db } from '../config';
import { collection, doc, updateDoc, where, arrayUnion, query, getDocs } from 'firebase/firestore';

// Re-applies a scroll position across several animation frames rather than
// just once — used to restore a list's scroll position after returning from
// ItemDetail (which shares the same `.content` scroll container and resets
// it to the top on open). A single rAF isn't reliable here: a freshly
// re-rendered list of many rows can take more than one frame to finish
// laying out, and scrollTop can't exceed scrollHeight - clientHeight until
// it does — setting it too early gets silently clamped back to 0. Cheap to
// just keep reapplying for a handful of frames rather than detecting when
// layout has "caught up".
export const restoreScrollPosition = (value, framesLeft = 8) => {
    requestAnimationFrame(() => {
        const contentEl = document.querySelector('.content');
        if (contentEl) contentEl.scrollTop = value;
        if (framesLeft > 1) restoreScrollPosition(value, framesLeft - 1);
    });
}

// A newly-added item's starting own/need quantities depend on which screen
// the user was on when they added it — e.g. adding from the Need tab means
// they're telling us they still need to get it, not that they already have
// it, so it should start pre-counted as 1 needed rather than landing
// unsorted (0/0) and forcing a redundant stepper tap right after typing the
// name. My Gear stays 0/0 — that screen has no such signal either way.
export const getDefaultQuantitiesForScreen = (screen) => {
    if (screen === 'need') return { ownQuantity: 0, needQuantity: 1 };
    if (screen === 'pack') return { ownQuantity: 1, needQuantity: 0 };
    return { ownQuantity: 0, needQuantity: 0 };
}

export const generateId = (length) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for ( let i = 0; i < length; i++ ) {
        result += characters.charAt(Math.floor(Math.random() *  charactersLength));
    }
    return result;
}

// An item's `categories` field is normally a single-element array
// (['Essentials']), but a plain string ('Essentials') can now show up too —
// the admin's Master packing list moved from multi-select to a single
// SelectInput that stores a bare string, and Root.js's populateItems spreads
// a defaultitems doc's fields directly onto a freshly-populated group's own
// items, so a new group can inherit that string shape. Every read of
// item.categories should go through this so both shapes work everywhere.
export const getItemCategories = (item) => {
    if (Array.isArray(item?.categories)) return item.categories;
    if (item?.categories) return [item.categories];
    return [];
}

// A festival's coordinates have been written a few different ways over
// time: plain numbers on location_lat/location_lng (the Places Autocomplete
// flow and scripts/geocode-festivals.js both write this shape), Firestore
// GeoPoints on those same two fields (possible via manual Firebase Console
// entry), a single GeoPoint on a location_geopoint field instead, or — seen
// on live data — a single GeoPoint on location_lat itself that holds both
// latitude and longitude with no location_lng field at all. Every read of a
// festival's coordinates should go through these so all four shapes work
// everywhere.
export const getFestivalLat = (festival) => {
    if (festival?.location_lat?.latitude !== undefined) return festival.location_lat.latitude;
    if (typeof festival?.location_lat === 'number') return festival.location_lat;
    if (festival?.location_geopoint?.latitude !== undefined) return festival.location_geopoint.latitude;
    if (typeof festival?.location_geopoint?.lat === 'number') return festival.location_geopoint.lat;
    return null;
}

export const getFestivalLng = (festival) => {
    if (festival?.location_lng?.longitude !== undefined) return festival.location_lng.longitude;
    if (typeof festival?.location_lng === 'number') return festival.location_lng;
    if (festival?.location_geopoint?.longitude !== undefined) return festival.location_geopoint.longitude;
    if (typeof festival?.location_geopoint?.lng === 'number') return festival.location_geopoint.lng;
    if (festival?.location_lat?.longitude !== undefined) return festival.location_lat.longitude;
    return null;
}

// Match a typed item name against a catalog list (a festival's items array,
// or defaultitems) — exact name match first, falling back to the `aka`
// alias field. Mirrors the lookup ItemDetail.js already uses for pro tips.
export const matchCatalogItem = (name, itemInfoList) => {
    if (!name || !itemInfoList?.length) return null;
    const itemName = name.toLowerCase().trim();
    let match = itemInfoList.find(i => i.name?.toLowerCase().trim() === itemName);
    if (!match) {
        match = itemInfoList.find(i => {
            const aka = i.aka?.toLowerCase().split(',').map(s => s.trim());
            return aka?.includes(itemName);
        });
    }
    return match || null;
}

// Builds and saves new item(s) into a group's items array from plain names,
// resolving hire/tip/aka via the catalog (a festival's own items array, or
// defaultitems for a custom event) — the same lookup and item shape
// AddSheet.js/InlineAdd.js already use for a manually-typed add.
//
// Defaults to prepending (sorts to the top of its category), matching
// AddSheet.js/InlineAdd.js — those add from a control at the top of the
// list, so the new item lands right where the user is looking. Pass
// position: 'bottom' for a control that instead sits at the bottom of the
// list (SuggestionChips) — prepending there would add the item out of the
// visible area with no feedback that anything happened, since the tapped
// chip is below everything already in the category.
export const addGearItems = async (group, names, category, itemInfoList, userUid, position = 'top') => {
    const existing = group.items || [];
    const categoryItems = existing.filter(item => getItemCategories(item).includes(category));
    const baseOrder = position === 'bottom'
        ? (categoryItems.length > 0 ? Math.max(...categoryItems.map(item => item.order ?? 0)) + 1 : 0)
        : Math.min(0, ...existing.map(item => item.order ?? 0)) - names.length;
    const newItems = names.map((name, i) => {
        const match = matchCatalogItem(name, itemInfoList);
        return {
            id: generateId(20),
            name,
            categories: [category],
            order: baseOrder + i,
            addedManually: true,
            organise: [{ assigned: userUid || 'unassigned', method: null, checked: false }],
            ownQuantity: 0,
            needQuantity: 0,
            tip: match?.tip || null,
            hire: match?.hire || false,
            aka: match?.aka || null,
        };
    });
    const updatedItems = position === 'bottom' ? [...existing, ...newItems] : [...newItems, ...existing];
    const groupRef = doc(db, 'groups', group.id);
    await updateDoc(groupRef, { items: updatedItems });
    return newItems;
}

export const sendNotification = async (notification, email) => {
    if (!notification) return;
    console.log('notification',email,notification);
    const recipientEmail =  email ? email : null;
    const recipientId = notification.recipientId ? notification.recipientId : null;
    if (recipientEmail) {
        try {
            //get target user via email
            const q = query(collection(db, "users"), where("email", "==", recipientEmail));
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach( async(document) => {
                await updateDoc(document.ref, {
                    //add to existing notifications in firebase
                    notifications: arrayUnion(notification)
                    });
               });      
        } catch (err) {
            console.log(err);
        } 
    } else if (recipientId) {
        try {
            //get target user via id
            const q = query(collection(db, "users"), where("id", "==", recipientId));
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach( async(document) => {
                await updateDoc(document.ref, {
                    //add to existing notifications in firebase
                    notifications: arrayUnion(notification)
                    });
               });      
        } catch (err) {
            console.log(err);
        } 
    }
}

